import { RollbackOutlined, SaveOutlined } from '@ant-design/icons'
import { Button, Checkbox, DatePicker, Form, Input, Radio, Spin } from 'antd'
import { FormInstance } from 'antd/lib/form'
import { differenceInDays, parse } from 'date-fns'
import moment from 'moment'
import PropTypes from 'prop-types'
import { isNil, pickBy } from 'ramda'
import React, { RefObject } from 'react'
import { connect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirestore } from 'react-redux-firebase'
import { withRouter } from 'react-router-dom'
import { withHandlers } from 'recompose'
import { compose } from 'redux'

import * as notificationActions from '../../actions/notifications'
import { DATE_FORMAT } from '../../constants/dates'
import { findInOrdered, fsCollections } from '../../helpers/firestore'
import { age } from '../../helpers/players'
import { CountrySelect } from '../countries'
import './PlayerForm.css'

const validateBirthDate = (rule, value, callback) =>
  !value || differenceInDays(new Date(), parse(value.toDate())) > 0 ? callback() : callback(false)

const dateFormat = 'DD.MM.YYYY'

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
    lg: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
}

const tailFormItemLayout = {
  wrapperCol: {
    xs: {
      span: 24,
      offset: 0,
    },
    sm: {
      span: 16,
      offset: 8,
    },
  },
}

class PlayerForm extends React.Component<any, any> {
  static propTypes = {
    player: PropTypes.object,
    onSubmit: PropTypes.func.isRequired,
    previousLocation: PropTypes.object,
    goToPreviousLocation: PropTypes.func.isRequired,
  }

  formRef: RefObject<FormInstance<any>> = React.createRef()

  handleSubmit = (values) => {
    const { onSubmit } = this.props

    const normalizeValues = (vs: FIXME) => {
      const toPersist: any = pickBy((value) => !isNil(value), vs)

      return {
        ...toPersist,
        birthDate: values.birthDate.format(DATE_FORMAT),
      }
    }

    onSubmit(normalizeValues(values))
  }

  handleFailure = ({ errorFields }) => {
    this.formRef?.current?.scrollToField(errorFields[0].name)
    /* TODO display somewhere */
    // tslint:disable-next-line:no-console
    console.log('Error during validation', errorFields)
  }

  render() {
    const { playerId, player, previousLocation, goToPreviousLocation, location } = this.props
    const { getFieldValue } = this.formRef.current
      ? this.formRef.current
      : { getFieldValue: () => undefined }
    const editing = location.pathname && location.pathname.includes('edit')

    const form = (
      <div className="container ant-col-12 ant-col-offset-6 PlayerForm">
        <h3>{`${editing ? 'Edit' : 'Add'} Player`}</h3>
        <Form
          ref={this.formRef}
          onFinish={this.handleSubmit}
          onFinishFailed={this.handleFailure}
          initialValues={{
            ...player,
            birthDate: player ? moment(player.birthDate) : undefined,
          }}>
          <Form.Item
            {...formItemLayout}
            label="First Name"
            hasFeedback
            name="firstName"
            rules={[{ required: true, message: 'Please input first name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label="Last Name"
            hasFeedback
            name="lastName"
            rules={[{ required: true, message: 'Please input last name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label="Gender"
            hasFeedback
            name="gender"
            rules={[{ required: true, message: 'Please select gender!' }]}>
            <Radio.Group>
              <Radio.Button value="female">Female</Radio.Button>
              <Radio.Button value="male">Male</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label="Nationality"
            hasFeedback
            name="nationality"
            rules={[{ required: true, message: 'Please select a country!' }]}>
            <CountrySelect showSelection />
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label="Birth Date"
            hasFeedback
            name="birthDate"
            rules={[
              { type: 'object', required: true, message: 'Please input birth date!' },
              {
                validator: validateBirthDate,
                message: 'Birth date must be in past!',
              },
            ]}
            extra={
              getFieldValue('birthDate') ? `Age is ${age(getFieldValue('birthDate'))} years` : null
            }>
            <DatePicker format={dateFormat} />
          </Form.Item>
          <Form.Item {...formItemLayout} label="Novice" name="novice" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <Form.Item {...formItemLayout} label="Address" name="address">
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label="E-mail"
            hasFeedback
            name="email"
            rules={[
              {
                type: 'email',
                message: 'The input is not a valid e-mail!',
              },
            ]}>
            <Input />
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label="Phone Number"
            hasFeedback
            name="phone"
            rules={[
              {
                pattern: /^[+]?[\d]+$/,
                message: 'The input is not a valid phone number!',
              },
            ]}>
            <Input />
          </Form.Item>
          <Form.Item {...formItemLayout} label="Club" name="club">
            <Input />
          </Form.Item>
          <Form.Item {...formItemLayout} label="ČMGS" name="czechRegistrationId">
            <Input />
          </Form.Item>
          <Form.Item {...tailFormItemLayout}>
            <Button.Group>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Submit
              </Button>
              {previousLocation ? (
                <Button danger onClick={goToPreviousLocation} icon={<RollbackOutlined />}>
                  Cancel
                </Button>
              ) : null}
            </Button.Group>
          </Form.Item>
        </Form>
      </div>
    )

    return !playerId || isLoaded(player) ? form : <Spin>{form}</Spin>
  }
}

export default compose<FIXME>(
  withFirestore,
  withRouter,
  firestoreConnect<FIXME>(({ tenant }) => [{ collection: fsCollections.globalPlayers(tenant) }]), // connecting just one doc ruins performance when reconnecting whole collection
  withHandlers({
    goToPreviousLocation: ({ history, previousLocation }) => () => {
      if (previousLocation) {
        history.push(previousLocation)
      }
    },
  }),
  withHandlers({
    onSubmitSuccess: ({ dispatch, goToPreviousLocation }) => () => {
      dispatch(notificationActions.notifySuccess('Player saved'))
      goToPreviousLocation()
    },
    onSubmitError: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error('Failed to save player data', err)
      dispatch(notificationActions.notifyFailure('Failed to save player data'))
    },
  }),
  withHandlers({
    onSubmit: ({ firestore, tenant, playerId, onSubmitSuccess, onSubmitError }) => (player) =>
      (playerId
        ? firestore.set({ collection: fsCollections.globalPlayers(tenant), doc: playerId }, player)
        : firestore.add({ collection: fsCollections.globalPlayers(tenant) }, player)
      )
        .then(onSubmitSuccess)
        .catch(onSubmitError),
  }),
  connect(({ firestore }: FIXME, { tenant, playerId }: FIXME) => ({
    player: findInOrdered(firestore.ordered[fsCollections.globalPlayers(tenant)], playerId),
  })),
)(PlayerForm)



// WEBPACK FOOTER //
// ./src/components/players/PlayerForm.tsx